// src/features/auth/api-layer/clients/endpoints.ts

/**
 * API Endpoints Configuration
 * Centralized endpoint definitions for the authentication system
 */

const API_VERSION = '/api/v1';

export const ENDPOINTS = {
  // Authentication Endpoints
  AUTH: {
    // Core Authentication
    LOGIN: `${API_VERSION}/auth/login`,
    LOGOUT: `${API_VERSION}/auth/logout`,
    REGISTER: `${API_VERSION}/auth/register`,
    REFRESH: `${API_VERSION}/auth/refresh`,
    ME: `${API_VERSION}/auth/me`,
    PROFILE: `${API_VERSION}/auth/profile`,

    // Password Management
    PASSWORD_RESET_REQUEST: `${API_VERSION}/auth/forgot-password`,
    PASSWORD_RESET_CONFIRM: `${API_VERSION}/auth/reset-password`,
    CHANGE_PASSWORD: `${API_VERSION}/auth/password/change`,
    UPDATE_PASSWORD: `${API_VERSION}/auth/password/update`,

    // Profile Management
    UPDATE_USERNAME: `${API_VERSION}/auth/profile/username`,
    UPDATE_BIO: `${API_VERSION}/auth/profile/bio`,
    UPDATE_PROFILE_PICTURE: `${API_VERSION}/auth/profile/picture`,
    UPDATE_EMAIL: `${API_VERSION}/auth/profile/email`,

    // Email Verification
    VERIFY_EMAIL: `${API_VERSION}/auth/email/verify`,
    RESEND_VERIFICATION: `${API_VERSION}/auth/email/resend-verification`,
    CHECK_EMAIL: `${API_VERSION}/auth/email/check-availability`,

    // Multi-Factor Authentication
    MFA_SETUP: `${API_VERSION}/auth/mfa/setup`,
    MFA_VERIFY: `${API_VERSION}/auth/mfa/verify`,
    MFA_DISABLE: `${API_VERSION}/auth/mfa/disable`,
    MFA_BACKUP_CODES: `${API_VERSION}/auth/mfa/backup-codes`,

    // Social Authentication
    SOCIAL_LOGIN: `${API_VERSION}/auth/social/login`,
    SOCIAL_LINK: `${API_VERSION}/auth/social/link`,
    SOCIAL_UNLINK: `${API_VERSION}/auth/social/unlink`,

    // Workspace/Tenant Management
    VALIDATE_WORKSPACE: `${API_VERSION}/auth/workspace/validate`,
    SWITCH_WORKSPACE: `${API_VERSION}/auth/workspace/switch`,

    // Invitation Flow
    VALIDATE_INVITE: `${API_VERSION}/auth/invite/validate`,
    ACCEPT_INVITE: `${API_VERSION}/auth/invite/accept`,

    // Company Code Flow
    VALIDATE_COMPANY_CODE: `${API_VERSION}/auth/company-code/validate`,

    // Device Management
    TRUST_DEVICE: `${API_VERSION}/auth/device/trust`,
    TRUSTED_DEVICES: `${API_VERSION}/auth/device/trusted`,

    // Session Management
    SESSIONS: `${API_VERSION}/auth/sessions`,
    REVOKE_ALL_SESSIONS: `${API_VERSION}/auth/sessions/revoke-all`,
  },

  // Tenant/Workspace Endpoints
  TENANT: {
    CURRENT: `${API_VERSION}/tenant/current`,
    CONFIG: `${API_VERSION}/tenant/config`,
    FEATURES: `${API_VERSION}/tenant/features`,
    THEME: `${API_VERSION}/tenant/theme`,
    BRANDING: `${API_VERSION}/tenant/branding`,
    USERS: `${API_VERSION}/tenant/users`,
    INVITE_USER: `${API_VERSION}/tenant/users/invite`,
    REMOVE_USER: `${API_VERSION}/tenant/users/remove`,
  },

  // User Management Endpoints
  USER: {
    PROFILE: `${API_VERSION}/user/profile`,
    PREFERENCES: `${API_VERSION}/user/preferences`,
    NOTIFICATIONS: `${API_VERSION}/user/notifications`,
    AVATAR: `${API_VERSION}/user/avatar`,
    SETTINGS: `${API_VERSION}/user/settings`,
  },

  // Permission Endpoints
  PERMISSIONS: {
    USER: `${API_VERSION}/permissions/user`,
    ROLE: `${API_VERSION}/permissions/role`,
    CHECK: `${API_VERSION}/permissions/check`,
  },

  // Security Endpoints
  SECURITY: {
    CSRF_TOKEN: `${API_VERSION}/security/csrf-token`,
    DEVICE_FINGERPRINT: `${API_VERSION}/security/device-fingerprint`,
    AUDIT_LOG: `${API_VERSION}/security/audit-log`,
  },
} as const;

/**
 * WebSocket Endpoints
 */
export const WS_ENDPOINTS = {
  AUTH_STATUS: '/ws/auth-status',
  TOKEN_REFRESH: '/ws/token-refresh',
  SESSION_SYNC: '/ws/session-sync',
} as const;

/**
 * External Service Endpoints
 */
export const EXTERNAL_ENDPOINTS = {
  // OAuth Provider Endpoints
  OAUTH: {
    GOOGLE: {
      AUTH: 'https://accounts.google.com/o/oauth2/v2/auth',
      TOKEN: 'https://oauth2.googleapis.com/token',
      USERINFO: 'https://www.googleapis.com/oauth2/v2/userinfo',
    },
    MICROSOFT: {
      AUTH: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      TOKEN: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
      USERINFO: 'https://graph.microsoft.com/v1.0/me',
    },
    GITHUB: {
      AUTH: 'https://github.com/login/oauth/authorize',
      TOKEN: 'https://github.com/login/oauth/access_token',
      USERINFO: 'https://api.github.com/user',
    },
  },
} as const;

/**
 * Dynamic Endpoint Builders
 */
export const buildEndpoint = {
  // User specific endpoints
  user: (userId: string) => ({
    profile: `${API_VERSION}/users/${userId}/profile`,
    permissions: `${API_VERSION}/users/${userId}/permissions`,
    sessions: `${API_VERSION}/users/${userId}/sessions`,
  }),

  // Tenant specific endpoints
  tenant: (tenantId: string) => ({
    details: `${API_VERSION}/tenants/${tenantId}`,
    users: `${API_VERSION}/tenants/${tenantId}/users`,
    settings: `${API_VERSION}/tenants/${tenantId}/settings`,
    billing: `${API_VERSION}/tenants/${tenantId}/billing`,
  }),

  // Resource specific endpoints
  resource: (resource: string, id: string) => ({
    get: `${API_VERSION}/${resource}/${id}`,
    update: `${API_VERSION}/${resource}/${id}`,
    delete: `${API_VERSION}/${resource}/${id}`,
  }),
};

/**
 * Endpoint Helper Functions
 */
export const endpointHelpers = {
  /**
   * Add query parameters to an endpoint
   */
  withParams: (endpoint: string, params: Record<string, unknown>): string => {
    const queryString = new URLSearchParams(params as Record<string, string>).toString();
    return queryString ? `${endpoint}?${queryString}` : endpoint;
  },

  /**
   * Replace path parameters in an endpoint
   */
  withPathParams: (endpoint: string, params: Record<string, string>): string => {
    let result = endpoint;
    Object.entries(params).forEach(([key, value]) => {
      result = result.replace(`:${key}`, value);
    });
    return result;
  },

  /**
   * Build full URL from endpoint
   */
  fullUrl: (endpoint: string, baseUrl?: string): string => {
    const base = baseUrl || process.env.VITE_API_BASE_URL || '';
    return `${base}${endpoint}`;
  },
};

/**
 * Type exports for endpoint usage
 */
export type EndpointKeys = keyof typeof ENDPOINTS;
export type AuthEndpoints = keyof typeof ENDPOINTS.AUTH;
export type TenantEndpoints = keyof typeof ENDPOINTS.TENANT;

// Freeze objects to prevent modification
Object.freeze(ENDPOINTS);
Object.freeze(WS_ENDPOINTS);
Object.freeze(EXTERNAL_ENDPOINTS);